import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { TranslateModule } from '@ngx-translate/core';
import { UserProfileDropdownComponent } from '../../../shared/components/user-profile-dropdown/user-profile-dropdown.component';
import { ThemeToggleComponent } from '../../../shared/components/theme-toggle/theme-toggle.component';
import { AuthService } from '../../services/auth/auth.service';
import { CompanyService } from '../../services/company/company.service';
import { PermissionsService } from '../../services/auth/permissions.service';

interface UserMenuItem {
  label: string
  path: string
}

interface NavLink {
  label: string
  path: string
  /** Módulo requerido para ver el enlace (oculta el módulo si no hay permiso). */
  module?: string
  /** Visible si tiene permiso en al menos uno de estos módulos (contenedores). */
  anyModules?: string[]
}

interface HeaderUserProfile {
  name: string
  email: string
  displayName: string
  accountInfo: string
  isRoot: boolean
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, UserProfileDropdownComponent, ThemeToggleComponent],
  templateUrl: './header.component.html'
})
export class HeaderComponent implements OnInit, OnDestroy {
  isUserMenuOpen = false
  isMobileMenuOpen = false
  isLoggingOut = false
  private companyInfoSub?: Subscription

  private readonly fallbackName = 'Usuario'
  private readonly fallbackEmail = 'correo@signly.com'
  private readonly fallbackAccountInfo = 'Cuenta de negocio'

  userProfile: HeaderUserProfile = {
    name: '',
    email: '',
    displayName: '',
    accountInfo: '',
    isRoot: false
  }

  readonly navLinks: NavLink[] = [
    { label: "HEADER.NAV.HOME", path: "/home" },
    { label: "HEADER.NAV.DOCUMENTS", path: "/documents", module: "documents" },
    { label: "HEADER.NAV.TEMPLATES", path: "/templates", module: "templates" },
    {
      label: "HEADER.NAV.ADMINISTRATION",
      path: "/administration",
      anyModules: ["users", "roles", "company", "webhooks", "audit"]
    },
  ]

  /** Enlaces visibles según permisos (se recalcula al resolver/actualizar permisos). */
  visibleNavLinks: NavLink[] = []
  private permissionsSub?: Subscription

  readonly userMenuItems: UserMenuItem[] = [
    { label: "USER_PROFILE.MY_PROFILE", path: "/users" },
    { label: "USER_PROFILE.PREFERENCES", path: "/settings" },
    { label: "USER_PROFILE.NOTIFICATIONS", path: "/notifications" },
  ]

  constructor(
    private elementRef: ElementRef,
    private authService: AuthService,
    private companyService: CompanyService,
    private permissionsService: PermissionsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.hydrateUserFromSession()
    this.subscribeToCompanyInfo()
    // Carga permisos (usa caché si existe → sin peticiones) y oculta módulos sin acceso.
    this.permissionsService.ensureLoaded()
    this.permissionsSub = this.permissionsService.state$.subscribe(() => {
      this.visibleNavLinks = this.navLinks.filter(link => this.isLinkVisible(link))
    })
  }

  ngOnDestroy(): void {
    this.companyInfoSub?.unsubscribe()
    this.permissionsSub?.unsubscribe()
  }

  private isLinkVisible(link: NavLink): boolean {
    if (link.module) {
      return this.permissionsService.canAccessModule(link.module)
    }
    if (link.anyModules?.length) {
      return link.anyModules.some(mod => this.permissionsService.canAccessModule(mod))
    }
    return true
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen
    if (this.isMobileMenuOpen) {
      this.isUserMenuOpen = false
    }
  }

  toggleUserMenu(event: Event): void {
    event.stopPropagation()
    this.isUserMenuOpen = !this.isUserMenuOpen
    if (this.isUserMenuOpen) {
      this.isMobileMenuOpen = false
    }
  }

  closeMenus(): void {
    this.isUserMenuOpen = false
    this.isMobileMenuOpen = false
  }

  handleManageProfile(): void {
    // Pendiente: la vista de administración de perfil aún no está implementada.
  }

  handleLogout(): void {
    if (this.isLoggingOut) {
      return
    }

    this.isLoggingOut = true
    this.authService
      .logout()
      .pipe(finalize(() => (this.isLoggingOut = false)))
      .subscribe({
        next: () => {
          this.permissionsService.reset()
          this.router.navigate(['/login'])
        },
        error: () => {
          this.permissionsService.reset()
          this.router.navigate(['/login'])
        }
      })
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeMenus()
    }
  }

  get dropdownUserName(): string {
    return this.userProfile.name || this.userProfile.displayName || this.fallbackName
  }

  get dropdownUserEmail(): string {
    return this.userProfile.email || this.fallbackEmail
  }

  get dropdownAccountInfo(): string {
    return this.userProfile.accountInfo || this.fallbackAccountInfo
  }

  get dropdownDisplayName(): string {
    return this.userProfile.displayName || this.userProfile.name || this.fallbackName
  }

  get userInitials(): string {
    const source = this.dropdownDisplayName || this.dropdownUserEmail
    if (!source) {
      return this.fallbackName.substring(0, 2).toUpperCase()
    }

    const normalized = source.trim()
    if (!normalized) {
      return this.fallbackName.substring(0, 2).toUpperCase()
    }

    const parts = normalized.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }

    return normalized.substring(0, Math.min(2, normalized.length)).toUpperCase()
  }

  private hydrateUserFromSession(): void {
    const session = this.authService.getSession()
    if (!session?.user) {
      return
    }

    const { name, email, isRoot } = session.user
    this.userProfile = {
      ...this.userProfile,
      name: name || this.userProfile.name,
      displayName: name || this.userProfile.displayName || email || '',
      email: email || this.userProfile.email,
      isRoot: !!isRoot
    }
  }

  private subscribeToCompanyInfo(): void {
    // Consumimos el stream del CompanyService. La precarga ya la realiza
    // LayoutComponent, así que aquí nunca disparamos una petición adicional.
    const cached = this.companyService.getCachedGeneralInfo()
    if (cached) {
      this.applyAccountInfo(cached.display_name || cached.legal_name)
    }
    this.companyInfoSub = this.companyService.generalInfoChanges().subscribe(info => {
      if (info) {
        this.applyAccountInfo(info.display_name || info.legal_name)
      }
    })
  }

  private applyAccountInfo(value: string | undefined | null): void {
    const accountDisplay = value || this.userProfile.accountInfo
    this.userProfile = {
      ...this.userProfile,
      accountInfo: accountDisplay
    }
  }
}
